import { toPng, toSvg } from 'html-to-image';
import type { RefObject } from 'react';
import * as ReactDOMServer from 'react-dom/server';

type Props = {
  title: string;
  queryLabel: string;
};

const Container = ({ title, queryLabel }: Props) => {
  return (
    <>
      <h1 className='text-primary text-base font-semibold text-balance'>{title}</h1>
      <p className='text-muted-foreground w-3/4 text-center text-wrap'>{queryLabel}</p>
      <p className='text-muted-foreground'>Källa: Booli</p>
    </>
  );
};

const createExportWrapper = (chartClone: HTMLElement, title: string, queryLabel: string, unit?: string) => {
  const wrapper = document.createElement('div');
  wrapper.style.display = 'flex';
  wrapper.style.alignItems = 'center';

  if (unit) {
    const unitLabel = document.createElement('div');
    unitLabel.textContent = unit;
    unitLabel.style.writingMode = 'vertical-rl';
    unitLabel.style.transform = 'rotate(180deg)';
    unitLabel.style.fontSize = '14px';
    unitLabel.style.color = '#6b7280';
    unitLabel.style.paddingRight = '8px';
    wrapper.appendChild(unitLabel);
  }

  const contentColumn = document.createElement('div');
  contentColumn.style.display = 'flex';
  contentColumn.style.flexDirection = 'column';
  contentColumn.style.alignItems = 'center';

  const titleContainer = document.createElement('div');
  titleContainer.style.display = 'flex';
  titleContainer.style.flexDirection = 'column';
  titleContainer.style.alignItems = 'center';
  titleContainer.style.width = '100%';
  titleContainer.innerHTML = ReactDOMServer.renderToStaticMarkup(
    <Container
      title={title}
      queryLabel={queryLabel}
    />,
  );

  // Remove the original export container from the chart clone
  const originalExportContainer = chartClone.querySelector('#export-container');
  if (originalExportContainer) {
    originalExportContainer.remove();
  }

  // Assemble: title + chart
  contentColumn.appendChild(titleContainer);
  contentColumn.appendChild(chartClone);

  // Assemble: unit + content
  wrapper.appendChild(contentColumn);

  return wrapper;
};

export const downloadPNG = async (
  chartRef: RefObject<HTMLDivElement | null>,
  title: string,
  queryLabel: string,
  unit?: string,
) => {
  const currentRef = chartRef?.current;
  if (!currentRef) return;

  const hiddenContainer = document.createElement('div');
  hiddenContainer.style.position = 'fixed';
  hiddenContainer.style.top = '0';
  hiddenContainer.style.left = '0';
  hiddenContainer.style.zIndex = '-9999';
  hiddenContainer.style.opacity = '0';
  hiddenContainer.style.pointerEvents = 'none';

  const chartClone = currentRef.cloneNode(true) as HTMLElement;
  const exportWrapper = createExportWrapper(chartClone, title, queryLabel, unit);

  hiddenContainer.appendChild(exportWrapper);
  document.body.appendChild(hiddenContainer);

  try {
    const dataUrl = await toPng(exportWrapper, {
      cacheBust: true,
      pixelRatio: 2,
    });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${title}.png`;
    a.click();
  } catch (error) {
    console.error('Error generating PNG:', error);
  } finally {
    document.body.removeChild(hiddenContainer);
  }
};

export const downloadSVG = async (
  chartRef: RefObject<HTMLDivElement | null>,
  title: string,
  queryLabel: string,
  unit?: string,
) => {
  const currentRef = chartRef?.current;
  if (!currentRef) return;

  const chartClone = currentRef.cloneNode(true) as HTMLElement;
  const exportWrapper = createExportWrapper(chartClone, title, queryLabel, unit);

  document.body.appendChild(exportWrapper);

  try {
    const dataUrl = await toSvg(exportWrapper, { cacheBust: true });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${title}.svg`;
    a.click();
  } catch (error) {
    console.error('Error generating SVG:', error);
  } finally {
    exportWrapper.remove();
  }
};
